import type { MyProfilePayload } from '../backend.d'
import { Component, createEffect, JSX, on, Show } from 'solid-js'
import { backendAxios } from '../axios'
import toastError from '../lib/AlertBag/toastError'
import { createCanvasElement } from '../lib/canvas/create_canvas'
import { getCanvas2dContext } from '../lib/canvas/get_canvas_2d_context'
import Unexpected from '../lib/Exception/Unexpected.class'
import createFileIdToObjectUrlSignal from '../lib/sb/storage/createFileIdToObjectUrlSignal'
import { updateAuthProfileValue } from '../rx/shared/profile/authProfileValue'
import { getAuthProfile } from '../rx/shared/profile/createAuthProfileSignal'
import { createLoadingSignal } from '../rx/signal/create_loading_signal'
import { isOfflineOnly } from '../lib/core/isOfflineOnly'
import { saveAvatar, generateAvatarId, deleteAvatar } from '../lib/idb/avatars'
// import { supabase } from '../supabase'

interface Props {
  size: number
  // url: Nullable<string>
  onUpload: (event: Event, filePath: string) => void
}

const Avatar: Component<Props> = (props) => {
  const loading = createLoadingSignal()
  function getAvatarId() {
    return getAuthProfile()?.avatar_id
  }
  const [avatarBlob, setAvatarId] = createFileIdToObjectUrlSignal(getAvatarId())

  createEffect(on(getAvatarId, (newAvatarId) => setAvatarId(newAvatarId)))

  const uploadAvatar: JSX.EventHandler<HTMLInputElement, Event> = async (event) => {
    try {
      loading.start()

      const target = event.currentTarget
      const inputFile = target.files?.[0]
      if (!inputFile) {
        throw new Error('You must select an image to upload.')
      }

      const resizedFile = await fileToImage(inputFile)

      let fileId: number

      if (isOfflineOnly) {
        // Offline mode: Store avatar in IndexedDB
        // Avatars deviate from the normal user-chooses-disk philosophy.
        // In offline-only builds, IndexedDB is the primary storage for avatars,
        // allowing user customization without backend connectivity.
        fileId = await generateAvatarId()
        await saveAvatar(fileId, resizedFile)
      } else {
        // Online mode: Upload to backend and cache to IndexedDB
        // Backend is the primary storage; IndexedDB serves as a cache for offline viewing.
        const formData = new FormData()
        formData.append('file', resizedFile)
        formData.append('type', 'avatar')
        const response = await backendAxios.post(`/file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        })

        fileId = response.data.id
        if (!fileId) {
          throw new Unexpected('!fileId')
        }

        // Cache to IndexedDB for offline viewing
        await saveAvatar(fileId, resizedFile)
      }

      const payload = {
        avatar_id: fileId,
      }
      
      if (!isOfflineOnly) {
        await backendAxios.post(`/profile/me`, payload)
      }

      updateAuthProfileValue({
        ...getAuthProfile(),
        ...payload as MyProfilePayload,
      })

      props.onUpload(event, fileId.toString())
    } catch (error) {
      console.error(error)
      toastError(error)
    } finally {
      loading.end()
    }
  }

  const deleteAvatarClick = async () => {
    try {
      loading.start()

      const currentAvatarId = getAvatarId()
      
      if (currentAvatarId) {
        // Delete from IndexedDB
        await deleteAvatar(currentAvatarId)
      }

      // Update profile to remove avatar reference
      // In offline mode, this updates localStorage only
      // In online mode, this would sync to backend
      const payload = {
        avatar_id: null,
      }
      
      if (!isOfflineOnly) {
        await backendAxios.post(`/profile/me`, payload)
      }

      updateAuthProfileValue({
        ...getAuthProfile(),
        ...payload as MyProfilePayload,
      })

      // Clear the signal
      setAvatarId(null)
    } catch (error) {
      console.error(error)
      toastError(error)
    } finally {
      loading.end()
    }
  }

  return (
    <div style={{ width: `${props.size}px` }}>
      {avatarBlob() ? (
        <img
          src={avatarBlob()!}
          alt={avatarBlob() ? 'Avatar' : 'No image'}
          class="img-thumbnail"
          style={{ height: `${props.size}px`, width: `${props.size}px` }}
        />
      ) : (
        <div
          class="avatar no-image"
          style={{ height: `${props.size}px`, width: `${props.size}px` }}
        />
      )}
      <div style={{ width: `${props.size}px` }}>
        <label class="btn btn-primary w-100 mb-2" for="avatar-upload">
          {loading() ? 'Uploading ...' : 'Upload Avatar'}
        </label>
        <input
          type="file"
          id="avatar-upload"
          accept="image/*"
          onChange={uploadAvatar}
          disabled={loading()}
          hidden
        />
        <Show when={avatarBlob()}>
          <button
            class="btn btn-outline-danger w-100"
            onClick={deleteAvatarClick}
            disabled={loading()}
          >
            Remove Avatar
          </button>
        </Show>
      </div>
    </div>
  )
}

export default Avatar

const maxWidth = 256
const maxHeight = 256

/**
 * Loads a File as an HTMLImageElement, resolving once the image has decoded.
 * The temporary object URL is revoked automatically on success or failure.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load the image.'))
    }
    img.src = url
  })
}

/**
 * Resizes & centre-crops `file` to maxWidth × maxHeight and returns it as a
 * PNG File.  Uses canvas.toDataURL() + dataURLtoBlob() (atob/ArrayBuffer)
 * instead of canvas.toBlob() so it works reliably across all browsers,
 * including WebKit in headless/test environments.
 */
async function fileToImage(file: File): Promise<File> {
  // Stop the user from stubbing their own toe
  if (file && file.size > 25 * 1024 * 1024) { // 25 MB limit
    throw new Error('File too big.')
  }

  const img = await loadImage(file)
  const { width, height } = img

  // Create a canvas to resize and crop
  const aspectRatio = width / height
  const targetAspectRatio = 1
  const canvas = createCanvasElement()
  const ctx = getCanvas2dContext(canvas)

  if (!ctx) {
    throw new Unexpected('!ctx')
  }

  // Determine the crop dimensions
  let sourceWidth = width
  let sourceHeight = height
  let offsetX = 0
  let offsetY = 0

  if (aspectRatio > targetAspectRatio) {
    // Wider than target aspect ratio
    sourceWidth = height * targetAspectRatio
    offsetX = (width - sourceWidth) / 2
  } else if (aspectRatio < targetAspectRatio) {
    // Taller than target aspect ratio
    sourceHeight = width / targetAspectRatio
    offsetY = (height - sourceHeight) / 2
  }

  // Resize and centre-crop
  canvas.width = maxWidth
  canvas.height = maxHeight
  ctx.drawImage(
    img,
    offsetX, offsetY, sourceWidth, sourceHeight, // Source
    0, 0, maxWidth, maxHeight // Destination
  )

  // canvas.toDataURL() is synchronous and universally supported (incl. WebKit headless).
  // dataURLtoBlob() converts via atob/ArrayBuffer — no fetch() required, which means
  // it works even in WebKit test environments that block fetch() on data: URIs.
  const mimeType = 'image/png'
  const dataUrl = canvas.toDataURL(mimeType)
  const blob = dataURLtoBlob(dataUrl)

  if (blob.size === 0) {
    throw new Error('Failed to process the image.')
  }

  const processedFile = new File([blob], file.name, { type: mimeType })

  if (processedFile.size > 150 * 1024) {
    throw new Error('File too big.')
  }

  return processedFile
}

/**
 * Converts a base64 data URL to a Blob without using fetch().
 * Using atob + ArrayBuffer is universally supported and works in all browsers,
 * including WebKit headless environments where fetch(dataURL) can be blocked.
 */
function dataURLtoBlob(dataURL: string): Blob {
  const [header, base64Data] = dataURL.split(',')
  const mimeMatch = header.match(/:(.*?);/)
  const mime = mimeMatch ? mimeMatch[1] : 'image/png'
  const byteString = atob(base64Data)
  const ab = new ArrayBuffer(byteString.length)
  const ia = new Uint8Array(ab)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }
  return new Blob([ab], { type: mime })
}